import { Chip } from '@mui/material';
import {
    ColDef,
    GridOptions,
    ICellRendererParams,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import Link from 'components/ui/AppLink';
import React, { FC } from 'react';

import { baseUrls } from '../../../../utils/baseUrls.utils';
import AdminList from '../../../ui/admin/AdminList';
import {
    formatStatusDate,
    statusDetails,
    subscriptionTypeLabels,
} from './utils';
import { AdminUserChannelSubscriptionRow } from './types';

interface UserChannelSubscriptionsTableProps {
    subscriptions: AdminUserChannelSubscriptionRow[];
}

const getChannelUrl = (channelId: string) =>
    baseUrls.publisher.premiumChannels.edit.replace(
        ':_id',
        window.encodeURIComponent(channelId)
    );

const ChannelCell: FC<
    ICellRendererParams<AdminUserChannelSubscriptionRow, string>
> = ({ data, value }) =>
    data ? <Link to={getChannelUrl(data.channel._id)}>{value}</Link> : null;

const StatusCell: FC<ICellRendererParams<AdminUserChannelSubscriptionRow>> = ({
    data,
}) => {
    if (!data) {
        return null;
    }

    const status = statusDetails[data.subscriptionState];

    return (
        <Chip
            label={status.label}
            color={status.color}
            size="small"
            variant="outlined"
        />
    );
};

const getChannelName = ({
    data,
}: ValueGetterParams<AdminUserChannelSubscriptionRow>): string =>
    data?.channel.name || '';

const getPublisherName = ({
    data,
}: ValueGetterParams<AdminUserChannelSubscriptionRow>): string =>
    data?.channel.owner?.name || '-';

const getSubscriptionType = ({
    data,
}: ValueGetterParams<AdminUserChannelSubscriptionRow>): string =>
    data ? subscriptionTypeLabels[data.subscriptionType] : '';

const getStatus = ({
    data,
}: ValueGetterParams<AdminUserChannelSubscriptionRow>): string =>
    data ? statusDetails[data.subscriptionState].label : '';

const formatUpdatedDate = ({
    value,
}: ValueFormatterParams<
    AdminUserChannelSubscriptionRow,
    Date | null | undefined
>): string => formatStatusDate(value || undefined) || '-';

const compareDates = (first?: Date | null, second?: Date | null): number =>
    new Date(first || 0).getTime() - new Date(second || 0).getTime();

const columnDefs: ColDef<AdminUserChannelSubscriptionRow>[] = [
    {
        headerName: 'Channel',
        valueGetter: getChannelName,
        cellRenderer: ChannelCell,
        minWidth: 200,
        menuTabs: [],
    },
    {
        headerName: 'Publisher',
        valueGetter: getPublisherName,
        minWidth: 160,
        menuTabs: [],
    },
    {
        headerName: 'Subscription',
        valueGetter: getSubscriptionType,
        minWidth: 150,
        menuTabs: [],
    },
    {
        headerName: 'Status',
        valueGetter: getStatus,
        cellRenderer: StatusCell,
        minWidth: 120,
        menuTabs: [],
    },
    {
        headerName: 'Last updated',
        field: 'updated',
        valueFormatter: formatUpdatedDate,
        comparator: compareDates,
        minWidth: 180,
        menuTabs: [],
    },
];

const defaultColDef: ColDef<AdminUserChannelSubscriptionRow> = {
    flex: 1,
};

const gridOptions: GridOptions<AdminUserChannelSubscriptionRow> = {
    getRowId: ({ data }) => `${data?.channelId}-${data?.createdAt}`,
    pagination: false,
};

const listSx = {
    mt: 0,
    minHeight: 0,
};

const UserChannelSubscriptionsTable: FC<UserChannelSubscriptionsTableProps> = ({
    subscriptions,
}) => (
    <AdminList
        items={subscriptions}
        columns={columnDefs}
        showAdd={false}
        loading={false}
        showHeader={false}
        listPageHeader={false}
        showCaption={false}
        pagination={false}
        maxWidth="none"
        removeBottomOffset
        showTableWhenEmpty={false}
        gridDensity="compact"
        gridOptions={gridOptions}
        defaultColDef={defaultColDef}
        overrideListSx={listSx}
    />
);

export default UserChannelSubscriptionsTable;
